import { useState } from "react";
import { useForm } from "react-hook-form";
import { RotatingLines } from "react-loader-spinner";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import useDocumentTitle from "../../hooks/useDocumentTitle";
import { isObjectEmpty, xReqId } from "../../utils";

const TicketInfoPage = () => {
	const [identificationType, setIdentificationType] = useState("PASSPORT");
	const [residencePermitType, setResidencePermitType] = useState("VIGNETTE");
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const user = searchParams.get("user");
	const { register, formState, handleSubmit, watch } = useForm({
		mode: "all",
		defaultValues: {
			ticketCode: "",
			firstName: "",
			lastName: "",
			idNumber: "",
		},
	});

	const hasErrors = !isObjectEmpty(formState.errors);
	const valuesIsEmptyObject = isObjectEmpty(watch());

	const disabledBtn =
		hasErrors ||
		valuesIsEmptyObject ||
		watch("ticketCode")?.trim() === "" ||
		watch("firstName")?.trim() === "" ||
		watch("lastName")?.trim() === "" ||
		watch("idNumber")?.trim() === "" ||
		formState.isSubmitting;

	useDocumentTitle("Στοιχεία εισιτηρίου");

	const submitTicket = async () => {
		// Determine the API endpoint based on the user type.
		const endpoint = user === "foreigner" ? "identify" : "personalize";

		// Build the base URL for the API endpoint.
		const baseURL = "/api/ticket-office/";

		// Construct the ticket data object from the form inputs.
		const ticketData = {
			ticketCode: watch("ticketCode").trim(),
			firstName: watch("firstName").trim(),
			lastName: watch("lastName").trim(),
			documentType: identificationType === "RESIDENCE_PERMIT" ? residencePermitType : identificationType,
			documentId: watch("idNumber").trim(),
			// ...extraDataForPermitResident,
		};

		// Build the URL for the API endpoint.
		const url = `${baseURL}${endpoint}`;

		try {
			// Send a POST request to the API endpoint with the ticket data.
			const res = await fetch(url, {
				method: "POST",
				credentials: "include", // Include cookies for authentication.
				headers: {
					"Content-Type": "application/json",
					"x-request-id": xReqId,
				}, // Specify the request body content type.
				body: JSON.stringify(ticketData), // Serialize the ticket data to JSON.
			});

			// If the response is successful, navigate to the success page, otherwise go to catch and show the toast error.
			if (res.ok) {
				const responseData = await res.json();
				const payload = { ...ticketData, ...responseData };
				user === "over-67"
					? navigate("/ticket-office/print-ticket", {
							state: { payload },
						})
					: navigate("/ticket-office/success", {
							state: { payload: ticketData, type: "ticket-data" },
						});
			} else {
				throw res;
			}
		} catch (error) {
			const errorResponse = await error.text();
			const genericErrorMessage =
				"Προέκυψε σφάλμα συστήματος. Παρακαλώ δοκιμάστε αργότερα και εάν το πρόβλημα εξακολουθεί επικοινωνήστε με την ομάδα υποστήριξης.";
			try {
				const json = JSON.parse(errorResponse);
				const appError = json?.exceptions?.errors?.[0]?.errorDescription;
				toast.error(appError ?? genericErrorMessage);
			} catch {
				toast.error(genericErrorMessage);
			}
		}
	};

	if (!["foreigner", "over-67"].includes(user)) {
		return <Navigate to={"/ticket-office/choose-user"} />;
	}

	return (
		<MainContainer>
			<div className="col-lg-12">
				<Link to="/ticket-office/choose-user" className="btn btn-back mb-4">
					<svg className="govgr-svg-icon--caret govgr-svg-icon" focusable="false" viewBox="0 0 24 24" aria-hidden="true">
						<path d="M18,22V2L6,12L18,22z"></path>
					</svg>
					Πίσω
				</Link>
				<h1 className="mb-4" id="ticket-info-heading">
					Στοιχεία εισιτηρίου
				</h1>
				<div className="row">
					<div className="col-md-6">
						<form noValidate onSubmit={handleSubmit(submitTicket)}>
							<fieldset className="border-0 p-0 m-0 mb-5" aria-labelledby="ticket-info-heading">
								<legend className="visually-hidden">Στοιχεία εισιτηρίου</legend>
								<div>
									<label htmlFor="ticketCode" className="form-label">
										Κωδικός εισιτηρίου <span className="required">*</span>
									</label>
									<input
										required
										id="ticketCode"
										onInput={(e) => {
											e.target.value = e.target.value.replace(/\D/g, "");
										}}
										aria-describedby="ticketCode-help"
										{...register("ticketCode", {
											maxLength: {
												value: 12,
											},
											minLength: {
												value: 12,
											},
											pattern: {
												value: /^\d{12}$/,
											},
										})}
										className={`form-control form-control-lg ${formState.errors.ticketCode ? "is-invalid" : ""}`}
									/>
									<div
										id="ticketCode-help"
										className={`form-text ${formState?.errors?.ticketCode ? "error text-danger" : " "}`}
									>
										12ψήφιος κωδικός
									</div>
								</div>
							</fieldset>
							<fieldset className="border-0 p-0 m-0">
								<legend className="h2 mb-4">Προσωπικά στοιχεία</legend>
								<div className="mb-4">
									<label htmlFor="firstName" className="form-label">
										Όνομα <span className="required">*</span>
									</label>
									<input
										required
										id="firstName"
										aria-describedby={formState.errors.firstName ? "firstName-help" : undefined}
										{...register("firstName", {
											pattern: {
												value: user === "foreigner" ? /^[A-Za-z\s.'`-]+$/ : /^[Α-Ωα-ωάίόώέύήϊϋΆΊΌΈΉΏΎΫΪ\s.'`-¨]+$/,
											},
										})}
										className={`form-control form-control-lg ${formState.errors.firstName ? "is-invalid" : ""}`}
									/>
									{formState?.errors?.firstName && (
										<div
											id="firstName-help"
											className={`form-text ${formState?.errors?.firstName ? "error text-danger" : " "}`}
										>
											{user === "foreigner" ? "Λατινικοί χαρακτήρες όπως στο διαβατήριο" : "Ελληνικοί χαρακτήρες"}
										</div>
									)}
								</div>
								<div className="mb-4">
									<label htmlFor="lastName" className="form-label">
										Επώνυμο <span className="required">*</span>
									</label>
									<input
										required
										id="lastName"
										aria-describedby={formState.errors.lastName ? "lastName-help" : undefined}
										{...register("lastName", {
											pattern: {
												value: user === "foreigner" ? /^[A-Za-z\s.'`-]+$/ : /^[Α-Ωα-ωάίόώέύήϊϋΆΊΌΈΉΏΎΫΪ\s.'`-¨]+$/,
											},
										})}
										className={`form-control form-control-lg ${formState.errors.lastName ? "is-invalid" : ""}`}
									/>
									{formState?.errors?.lastName && (
										<div
											id="lastName-help"
											className={`form-text ${formState?.errors?.lastName ? "error text-danger" : " "}`}
										>
											{user === "foreigner" ? "Λατινικοί χαρακτήρες όπως στο διαβατήριο" : "Ελληνικοί χαρακτήρες"}
										</div>
									)}
								</div>
								<div className="mb-4">
									<label htmlFor="documentType" className="form-label">
										Τύπος πιστοποιητικού <span className="required">*</span>
									</label>
									<select
										id="documentType"
										className="form-select form-select-lg"
										value={identificationType}
										onChange={(e) => setIdentificationType(e.target.value)}
										required
									>
										<option value="PASSPORT">Διαβατήριο</option>
										<option value={user === "foreigner" ? "FOREIGN_ID" : "NATIONAL_ID"}>Ταυτότητα</option>
										{user === "foreigner" ? <option value="RESIDENCE_PERMIT">Άδεια Παραμονής</option> : null}
									</select>
								</div>
								{identificationType === "RESIDENCE_PERMIT" ? (
									<div className="mb-4">
										<label htmlFor="residentPermitType" className="form-label">
											Τύπος Άδειας Παραμονής <span className="required">*</span>
										</label>
										<select
											id="residentPermitType"
											className="form-select form-select-lg"
											value={residencePermitType}
											onChange={(e) => setResidencePermitType(e.target.value)}
											required
										>
											<option value="VIGNETTE">ΒΙΝΙΕΤΑ</option>
											<option value="CERTIFICATE_APPLICATION">ΒΕΒΑΙΩΣΗ ΚΑΤΑΘΕΣΗΣ</option>
											<option value="RESIDENCE_PERMIT">ΔΕΛΤΙΟ ΔΙΑΜΟΝΗΣ</option>
											<option value="RESIDENCE_PERMIT_PERMANENT">ΔΕΛΤΙΟ ΜΟΝΙΜΗΣ ΔΙΑΜΟΝΗΣ</option>
											<option value="RESIDENCE_PERMIT_SPECIAL">ΕΙΔΙΚΗ ΒΕΒΑΙΩΣΗ ΝΟΜΙΜΗΣ ΔΙΑΜΟΝΗΣ</option>
											<option value="STANDALONE_DOCUMENT">ΑΥΤΟΤΕΛΕΣ ΕΓΓΡΑΦΟ</option>
											<option value="ELECTRONIC_APPLICATION">ΗΛΕΚΤΡΟΝΙΚΗ ΑΙΤΗΣΗ</option>
										</select>
									</div>
								) : null}
								<div className="mb-4">
									<label htmlFor="idNumber" className="form-label">
										Αριθμός πιστοποιητικού <span className="required">*</span>
									</label>
									<input
										required
										id="idNumber"
										aria-describedby="idNumber-help"
										{...register("idNumber", {
											pattern: {
												value: /^[A-ZΑ-Ω0-9-]+$/,
												message: `Ο αριθμός πιστοποιητικού δεν είναι σωστός`,
											},
										})}
										className={`form-control form-control-lg ${formState.errors.idNumber ? "is-invalid" : ""}`}
									/>
									<div
										id="idNumber-help"
										className={`form-text ${formState?.errors?.idNumber ? "error text-danger" : " "}`}
									>
										{formState?.errors?.idNumber?.message}
									</div>
								</div>
							</fieldset>
							<button type="submit" disabled={disabledBtn} className="btn btn-primary btn-lg btn-login">
								{formState.isSubmitting ? (
									<div className="d-flex justify-content-center align-items-center">
										<RotatingLines
											visible={true}
											color="grey"
											strokeWidth="5"
											strokeColor="white"
											animationDuration="0.75"
											ariaLabel="rotating-lines-loading"
											wrapperStyle={{}}
											wrapperClass=""
										/>
										<div>Aποστολή</div>
									</div>
								) : (
									"Aποστολή"
								)}
							</button>
						</form>
					</div>
				</div>
			</div>
		</MainContainer>
	);
};

export default TicketInfoPage;
